import Tooltip from '@mui/material/Tooltip'
import { Bucket, WithIntl } from 'interfaces'
import { useState, useMemo, useEffect, useRef, Fragment } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { bucketGroupsSelector, bucketsSelector } from 'services/post/selectors'
import { AppNavLink } from '../../AppNavLink'
import sharedStyles from '../../AppNavigation.pcss'
import EditIcon from '@mui/icons-material/Edit'
import { DndContext, useDroppable, useSensor, useSensors, PointerSensor } from '@dnd-kit/core'
import { reorder } from 'shared/utils'
import { Subject } from 'rxjs/Subject'
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers'
import { StoreThunkDispatch } from 'store/state'
import { reorderBuckets, updateBucketGroup } from 'services/post/actions'
import { catchError } from 'rxjs/operators/catchError'
import { Observable } from 'rxjs/Observable'
import { message } from 'services/snackbar'
import { PPNavLink } from 'components/PPNavLink'
import { BucketGroup, MAX_BUCKET_GROUP_NAME_LENGTH } from 'shared'
import Dialog from '@mui/material/Dialog'
import Button from '@mui/material/Button'
import Input from '@mui/material/Input'
import styles from './BucketsNavMenu.pcss'

const GROUP_ID_EMPTY = 'no-group'

interface BucketsNavMenuProps {
  expanded: boolean
  onToggleExpanded: () => void
  onCreateClick: () => void
  onEditBucketClick: (bucket: Bucket) => void
}

export function BucketsNavMenu(props: BucketsNavMenuProps & WithIntl) {
  const location = useLocation()
  const dispatch: StoreThunkDispatch = useDispatch()
  const groups = useSelector(bucketGroupsSelector)
  const buckets = useSelector(bucketsSelector)
  const [expandedGroupId, setExpandedGroupId] = useState<null | string>(null)
  const [editGroup, setEditGroup] = useState({ id: null, name: '' })

  const groupsWithBuckets: Array<BucketGroup & { buckets: Map<string, Bucket> }> = useMemo(() => {
    return groups.reduce((data, group) => {
      const bucketsMap = new Map(
        buckets
          .filter(b => b.groupId === group.id)
          .map(b => [b.id, b])
      )
      const withBuckets = {
        ...group,
        buckets: bucketsMap,
        bucketIds: [...bucketsMap.keys()]
      }
      if ([...withBuckets.buckets.keys()].length === 0) {
        return data
      }
      if (data.length === 0) {
        return [withBuckets]
      }
      return data.concat([withBuckets])
    }, [] as Array<BucketGroup & { buckets: Map<string, Bucket> }>)
  }, [groups, buckets])

  const bucketsNoGroup = useMemo(() => {
    return new Map(
      buckets
        .filter(b => !b.groupId)
        .map(bucket => [bucket.id, bucket])
    )
  }, [buckets])

  const [order, setOrder] = useState<Record<string, string[]>>({})

  useEffect(() => {
    setOrder({
      [GROUP_ID_EMPTY]: Array.from(bucketsNoGroup.keys()),
      ...groupsWithBuckets.reduce((acc, group) => {
        acc[group.id] = group.bucketIds
        return acc
      }, {} as Record<string, string[]>)
    })
  }, [groupsWithBuckets, bucketsNoGroup])

  const toggleGroupExpanded = (id: string) => {
    setExpandedGroupId(current => current === id ? null : id)
  }

  const reorder$ = useRef<Subject<string[]>>(undefined)

  useEffect(() => {
    reorder$.current = new Subject()
    reorder$.current.switchMap((ids) => {
      return Observable.fromPromise(dispatch(reorderBuckets(ids)).unwrap())
        .pipe(catchError((error) => {
          dispatch(message(props.intl.formatMessage({ id: 'notifications.generic.update-failed' }), 'error'))
          return Observable.of({ error })
        }))
    })
      .subscribe()
  }, [dispatch, props.intl])

  const createBucketButton = (
    <div key="add-bucket" className={`${sharedStyles['child-link']} ${sharedStyles['link-action']}`} onClick={props.onCreateClick}>
      + <FormattedMessage id="app.nav.publishing-buckets.create" />
    </div>
  )

  const onDragEnd = (event: any) => {
    const { active, over } = event
    if (active.id !== over.id) {
      const groupId = active.data.current?.groupId || GROUP_ID_EMPTY
      const oldIndex = order[groupId].indexOf(active.id)
      const newIndex = order[groupId].indexOf(over.id)
      const reordered = reorder(order[groupId], oldIndex, newIndex)
      setOrder({ ...order, [groupId]: reordered })
      reorder$.current?.next(reordered)
    }
  }

  const activeBucketId = location.pathname.replace('/posts/buckets/', '').split('/')[0]

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5
      }
    })
  )

  const cancelEvent = (e: any) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const onGroupEdit = (e: any) => {
    e.preventDefault()
    e.stopPropagation()
    setEditGroup({
      id: e.currentTarget.dataset.id,
      name: groups.find(g => g.id === e.currentTarget.dataset.id)?.name || ''
    })
  }

  const closeEditGroupPopup = () => {
    setEditGroup({ id: null, name: '' })
  }

  const updateGroup = () => {
    const id = editGroup.id
    const name = editGroup.name.trim()
    if (!name) {
      dispatch(message('Group name cannot be empty', 'error'))
      return
    }

    if (name.length > MAX_BUCKET_GROUP_NAME_LENGTH) {
      dispatch(message(`Group name is too long! Maximum length is ${MAX_BUCKET_GROUP_NAME_LENGTH} characters.`))
      return
    }

    if (!id) {
      return
    }
    dispatch(updateBucketGroup({ id, name }))
    closeEditGroupPopup()
  }

  const onGroupNameChange = (e: any) => {
    setEditGroup({ ...editGroup, name: e.target.value })
  }

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd} modifiers={[restrictToVerticalAxis]}>
      <div className={sharedStyles['buckets-nav']}>
        <AppNavLink
          label={<FormattedMessage id="app.nav.publishing-buckets" />}
          to="/posts/buckets"
          expanded={props.expanded}
          subNavClassName={sharedStyles['list-padded-bottom']}
          expandedItemKey={activeBucketId}
          small
          items={(
            <Fragment>
              {groupsWithBuckets.map(group => (
                <AppNavLink
                  id={group.id}
                  key={group.id}
                  to="/"
                  onClick={cancelEvent}
                  label={(
                    <div className={sharedStyles['link-label-box']}>
                      <Tooltip title={<FormattedMessage id="post.buckets.edit-bucket-group-tooltip" />} placement="top">
                        <EditIcon className={sharedStyles['btn-edit']} data-id={group.id} onClick={onGroupEdit} />
                      </Tooltip>
                      <span className="text-ellipsis">{group.name}</span>
                    </div>
                  )}
                  toggleExpandedOnClick
                  expanded={group.id === expandedGroupId}
                  onToggleExpanded={toggleGroupExpanded}
                  items={(
                    <SortableBucketList
                      buckets={group.buckets}
                      order={order[group.id] || []}
                      activeBucketId={activeBucketId}
                      groupId={group.id}
                      onEditBucket={props.onEditBucketClick}
                    />
                  )}
                  className={sharedStyles['bucket-group-link']}
                />
              ))}
              <SortableBucketList
                buckets={bucketsNoGroup}
                order={order[GROUP_ID_EMPTY] || []}
                activeBucketId={activeBucketId}
                onEditBucket={props.onEditBucketClick}
              />
              {createBucketButton}
            </Fragment>
          )}
          toggleExpandedOnClick
          onToggleExpanded={props.onToggleExpanded}
        />
      </div>
      <Dialog open={Boolean(editGroup.id)}>
        <h4 className={styles['popup-title']}>Rename group</h4>
        <div className={styles['popup-content']}>
          <Input
            type="text"
            value={editGroup.name}
            placeholder="Group name"
            className={styles['input-group-name']}
            onChange={onGroupNameChange}
          />
        </div>
        <div className={styles['popup-actions']}>
          <Button onClick={closeEditGroupPopup}>
            <FormattedMessage id="actions.cancel" />
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={`${styles['btn-submit']}`}
            onClick={updateGroup}
          >
            <FormattedMessage id="actions.save" />
          </Button>
        </div>
      </Dialog>
    </DndContext>
  )
}

interface SortableBucketListProps {
  buckets: Map<string, Bucket>
  order: string[]
  groupId?: string
  activeBucketId?: string
  onEditBucket: (b: Bucket) => void
}

function SortableBucketList({ buckets, order, activeBucketId, groupId, onEditBucket }: SortableBucketListProps) {
  const { setNodeRef } = useDroppable({ id: groupId || GROUP_ID_EMPTY })

  return (
    <SortableContext items={order} strategy={rectSortingStrategy} modifiers={[restrictToParentElement]}>
      <div ref={setNodeRef} className={sharedStyles['buckets-nav']}>
        {order.map(id => {
          const bucket = buckets.get(id)
          if (!bucket) {
            return null
          }
          return (
            <DraggableBucketLink
              key={bucket.id}
              active={bucket.id === activeBucketId}
              bucket={bucket}
              onEdit={onEditBucket}
            />
          )
        })}
      </div>
    </SortableContext>
  )
}

function DraggableBucketLink(props: { bucket: Bucket, active: boolean, onEdit: (b: Bucket) => void }) {
  const colorStyle = { backgroundColor: props.bucket.color }

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.bucket.id,
    data: {
      groupId: props.bucket.groupId || GROUP_ID_EMPTY
    }
  })

  const onEdit = (e: any) => {
    e.preventDefault()
    e.stopPropagation()
    props.onEdit(props.bucket)
  }

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    pointerEvents: isDragging ? 'none' : 'all'
  } as any

  return (
    <div ref={setNodeRef} style={style}>
      <AppNavLink
        to={`/posts/buckets/${props.bucket.id}`}
        className={sharedStyles['child-link']}
        small
        expanded={props.active}
        hideToggle
        label={(
          <div className={sharedStyles['link-label-box']} {...listeners} {...attributes}>
            <Tooltip title={<FormattedMessage id="post.buckets.edit-bucket-tooltip" />} placement="top">
              <EditIcon className={sharedStyles['btn-edit']} onClick={onEdit} />
            </Tooltip>
            <div style={colorStyle} className={sharedStyles['color-badge']}></div>
            <div className="text-ellipsis" title={props.bucket.name}>{props.bucket.name}</div>
            <span className={sharedStyles['b-posts']}>{`(${props.bucket.postsCount})`}</span>
          </div>
        )}
        items={(
          <Fragment>
            <PPNavLink
              to={`/posts/buckets/${props.bucket.id}/posts`}
              end
              className={`${sharedStyles['child-link']} ${sharedStyles['pad-left']}`}
              activeClassName={sharedStyles.active}
            >
              <FormattedMessage id="app.nav.publishing-bucket-posts" />
            </PPNavLink>
            <PPNavLink
              end
              to={`/posts/buckets/${props.bucket.id}/posted`}
              className={`${sharedStyles['child-link']} ${sharedStyles['pad-left']}`}
              activeClassName={sharedStyles.active}
            >
              <FormattedMessage id="app.nav.publishing-posted-posts" />
            </PPNavLink>
          </Fragment>
        )}
      />
    </div>
  )
}

export default injectIntl(BucketsNavMenu)
